import { Link, useParams } from "react-router-dom";
import {
  api,
  type EngagementParts,
  type EngagementSummary,
  type TeamMember,
} from "../lib/api";
import { useResource } from "../lib/useResource";
import { AppShell, ScreenHead } from "../shell/AppShell";
import {
  Empty,
  Failed,
  Label,
  Mark,
  SkeletonRows,
} from "../components/primitives";
import { classificationLabel, day } from "../lib/format";
import { roleLabel, useSession } from "../lib/session";

/**
 * The engagement, read.
 *
 * **What this screen is for.** What is this piece of work, for whom, what is
 * it about, what is it made of, and who is on it. Nothing here changes
 * anything. That is the point: it is the screen everybody on the engagement
 * can open, including a consultant, and everything on it is true for them.
 *
 * **Why it used to be wrong.** This screen carried the whole of setup —
 * rename, make active, add workstream, add milestone, bring someone in, mark
 * ready, open the portal — with no role check anywhere in the file. A
 * consultant was shown all seven, and the server refused all seven, because
 * every one of them asks `app.may_manage` (role AND reach >= 3) and a
 * consultant satisfies neither half. Seven buttons that look available and
 * do nothing is not a permissions bug; it is the interface lying about what
 * you can do, which is the "jagged" part.
 *
 * Setup now lives at `/engagements/:id/setup`, reachable only by the people
 * who can actually use it. The division is the one the user drew: Command is
 * what needs you today, Setup is shaping the thing, and this is the thing.
 */
export function Engagement() {
  const { id } = useParams<{ id: string }>();
  const engagementId = id ?? "";
  const { me } = useSession();

  const engagements = useResource<EngagementSummary[]>(
    () => api.get<EngagementSummary[]>("/api/engagements"),
    [],
  );
  const parts = useResource<EngagementParts>(
    () => api.get<EngagementParts>(`/api/engagements/${engagementId}/parts`),
    [engagementId],
  );
  const team = useResource<TeamMember[]>(
    () => api.get<TeamMember[]>(`/api/engagements/${engagementId}/team`),
    [engagementId],
  );

  const engagement =
    engagements.resource.state === "ready"
      ? engagements.resource.data.find((e) => e.id === engagementId)
      : undefined;

  return (
    <AppShell
      engagement={
        engagement && {
          reference: engagement.reference,
          name: engagement.name,
          organisation: engagement.organisation,
        }
      }
    >
      <ScreenHead title={engagement?.name ?? "Engagement"} />

      {engagements.resource.state === "loading" && <SkeletonRows rows={4} />}
      {engagements.resource.state === "failed" && (
        <Failed
          message={engagements.resource.message}
          correlationId={engagements.resource.correlationId}
        />
      )}
      {engagements.resource.state === "ready" && !engagement && (
        <Empty title="No such engagement">
          It may have been closed, or it may not be yours.
        </Empty>
      )}

      {engagement && (
        <>
          <Identity engagement={engagement} role={me?.role ?? ""} />
          <About engagement={engagement} />

          <section className="mt-12">
            <Label as="h2">What this engagement is made of</Label>
            {parts.resource.state === "failed" && (
              <Failed
                message={parts.resource.message}
                correlationId={parts.resource.correlationId}
              />
            )}
            {parts.resource.state === "loading" && <SkeletonRows rows={3} />}
            {parts.resource.state === "ready" && (
              <PartsList parts={parts.resource.data} />
            )}
          </section>

          <section className="mt-12">
            <Label as="h2">Who is on it</Label>
            {team.resource.state === "failed" && (
              <Failed
                message={team.resource.message}
                correlationId={team.resource.correlationId}
              />
            )}
            {team.resource.state === "loading" && <SkeletonRows rows={3} />}
            {team.resource.state === "ready" && (
              <Roster members={team.resource.data} />
            )}
          </section>
        </>
      )}
    </AppShell>
  );
}

/** Reference, client, classification, status, dates — and the way onward. */
function Identity({
  engagement,
  role,
}: {
  engagement: EngagementSummary;
  role: string;
}) {
  // The Desk is a manager's surface and is absent from a consultant's rail,
  // so linking to it from here would be the same lie in a quieter voice.
  const commandIsTheirs =
    role === "super_admin" || role === "principal_partner" || role === "project_lead";

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-line pb-4">
        <div className="min-w-0">
          <p className="font-mono text-xs text-secondary">
            {engagement.reference}
          </p>
          <p className="mt-1 text-base text-ink">{engagement.organisation}</p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <Mark>{classificationLabel(engagement.classification)}</Mark>
          <span className="text-sm text-secondary">{engagement.status}</span>
          {commandIsTheirs && (
            <Link
              to={`/engagements/${engagement.id}/desk`}
              className="text-sm text-secondary underline-offset-4 hover:text-ink hover:underline"
            >
              Open the Desk
            </Link>
          )}
          {engagement.mayManage && (
            <Link
              to={`/engagements/${engagement.id}/setup`}
              className="text-sm text-secondary underline-offset-4 hover:text-ink hover:underline"
            >
              Set up
            </Link>
          )}
        </div>
      </div>

      <dl className="mt-5 flex flex-wrap gap-x-10 gap-y-2 text-sm">
        <Fact label="Started" value={engagement.startedOn && day(engagement.startedOn)} />
        <Fact
          label="Target"
          value={engagement.targetCompletion && day(engagement.targetCompletion)}
        />
        <Fact
          label="Client portal"
          value={
            engagement.portalActivatedAt
              ? `Open since ${day(engagement.portalActivatedAt)}`
              : engagement.portalReadyAt
                ? "Ready, not open"
                : "Not prepared"
          }
        />
      </dl>
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="label">{label}</dt>
      <dd className="mt-1 text-ink">{value ?? "—"}</dd>
    </div>
  );
}

/**
 * The brief.
 *
 * A name is a label, not a description. "Intuiva front-end build" tells a
 * consultant who has just been put on the engagement nothing about what the
 * work is, and until migration 029 there was nowhere to say it.
 */
function About({ engagement }: { engagement: EngagementSummary }) {
  return (
    <section className="mt-12">
      <Label as="h2">What this is about</Label>
      {engagement.brief ? (
        // `whitespace-pre-line` so paragraph breaks the author typed survive.
        <p className="mt-3 max-w-[70ch] whitespace-pre-line text-base leading-relaxed text-ink">
          {engagement.brief}
        </p>
      ) : (
        <Empty title="No brief yet">
          {engagement.mayManage
            ? "Setup is where you write it."
            : "Nobody has written one. Ask whoever leads this engagement."}
        </Empty>
      )}
    </section>
  );
}

/**
 * Workstreams and their milestones, read only.
 *
 * Exported because Setup shows exactly the same list above its controls —
 * you should be able to see the shape you are editing without leaving the
 * screen, and two renderings of one list drift apart.
 */
export function PartsList({ parts }: { parts: EngagementParts }) {
  const loose = parts.milestones.filter((m) => !m.workstreamId);

  if (parts.workstreams.length === 0 && parts.milestones.length === 0) {
    return (
      <Empty title="No parts yet">
        An engagement works without them — they are how you give it shape when
        it is bigger than one piece of work.
      </Empty>
    );
  }

  return (
    <>
      {parts.workstreams.map((w) => (
        <Workstream
          key={w.id}
          name={w.name}
          milestones={parts.milestones.filter((m) => m.workstreamId === w.id)}
        />
      ))}
      {loose.length > 0 && (
        <Workstream name="Not in a workstream" quiet milestones={loose} />
      )}
    </>
  );
}

function Workstream({
  name,
  quiet,
  milestones,
}: {
  name: string;
  quiet?: boolean;
  milestones: EngagementParts["milestones"];
}) {
  return (
    <div className="border-b border-line py-3">
      <p className={`text-base ${quiet ? "text-secondary" : "text-ink"}`}>
        {name}
      </p>
      {milestones.length === 0 ? (
        <p className="mt-1 text-sm text-secondary">Nothing scheduled</p>
      ) : (
        <ul className="mt-1">
          {milestones.map((m) => (
            <li
              key={m.id}
              className="flex items-baseline justify-between gap-3 text-sm text-secondary"
            >
              <span>{m.completedAt ? `✓ ${m.name}` : m.name}</span>
              {m.dueOn && <span>{day(m.dueOn)}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Who is on the engagement, as a fact rather than a control panel.
 *
 * The editable version — extend, revoke, add — is Setup's. Here a consultant
 * can see who else is on the work and how to reach them, which is a question
 * they have every right to ask and had no screen that answered it.
 */
function Roster({ members }: { members: TeamMember[] }) {
  const live = members.filter(
    (m) => m.status === "accepted" || m.status === "active",
  );

  if (live.length === 0) {
    return <Empty title="Nobody on it yet" />;
  }

  return (
    <ul>
      {live.map((m) => (
        <li
          key={`${m.kind}-${m.userId}-${m.assignmentId ?? ""}`}
          className="flex flex-wrap items-baseline justify-between gap-3 border-b border-line py-3"
        >
          <div className="min-w-0">
            <p className="text-base text-ink">{m.fullName}</p>
            <p className="mt-1 break-all text-sm text-secondary">{m.email}</p>
          </div>
          <div className="flex flex-wrap items-baseline gap-4 text-sm text-secondary">
            {m.scope && (
              <span className="label">
                {m.scope === "engagement"
                  ? "Whole engagement"
                  : m.scope === "workstream"
                    ? "One workstream"
                    : "Assigned tasks only"}
              </span>
            )}
            <span>{m.kind === "client" ? "Client side" : roleLabel(m.role)}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}
